import { AttachmentModel } from "../../../../service/models/attachment_modal";
import { BaseModel } from "../../../../service/models/base";

export interface AddressModel {
  id?: string;
  user_id?: string;
  type?: string;
  latitude?: string;
  longitude?: string;
  branch_name?: string;
  line1: string;
  line2?: string;
  line3?: string;
  city: string;
  state: string;
  country: string;
  pincode: number;
}

export type ItemRow = {
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  isWacFetched?: boolean;
  itemUnit: string;
  subcategory: string;
  availableQty: number;
  itemHsn: string;
  requestedQty?: number;
};

export interface ItemWarehouseModel {
  inventoryItemCode: string;
  requestedQty: number;
  receivedQty?: number;
  pickedQty?: number;
  itemPrice?: number;
  subCategory: string;
  itemUnit: string;
  itemName: string;
  itemHSN: string;
  deviceIds?: string[];
  gst_applicable: boolean;
  tax_detail: TaxDetailBreakdown;
  source_order_id?: string;
}
export interface TaxDetailBreakdown {
  igst: number;
  cgst: number;
  sgst: number;
}
// Warehouses physically co-located in the same building. Mirrors the backend
// config in warehouse_outward_model.ts. An internal ST is only allowed between
// warehouses within the same group and skips docs + logistics.
export const INTERNAL_ST_WAREHOUSE_GROUPS: string[][] = [
  ["WRH-434261", "WRH-938723"], // HSR
  ["WRH-919746", "WRH-070995"], // Koramangala
  ["WRH-415128", "WRH-863887"], //Jobwork
];

export const getInternalStGroup = (
  warehouseId?: string,
): string[] | undefined => {
  if (!warehouseId) return undefined;
  return INTERNAL_ST_WAREHOUSE_GROUPS.find((g) => g.includes(warehouseId));
};

export const areWarehousesInSameInternalGroup = (
  fromId?: string,
  toId?: string,
): boolean => {
  if (!fromId || !toId || fromId === toId) return false;
  const group = getInternalStGroup(fromId);
  return !!group && group.includes(toId);
};

export interface CreateRegularSTPayload {
  from_warehouse_id: string;
  from_warehouse_name: string;
  to_warehouse_id: string;
  to_warehouse_name: string;
  expected_date: Date;
  purpose: string;
  description?: string;
  contact_name: string;
  contact_number: string;
  is_internal?: boolean;
  shipping_details?: {
    logistic_partner?: string;
    tracking_id?: string;
  };
  tracking_id?: string;
  items: ItemWarehouseModel[];
}

export interface CreateReturnableSTPayload {
  from_warehouse_id: string;
  from_warehouse_name: string;
  shipping_details?: {
    logistic_partner?: string;
    tracking_id?: string;
  };
  destination_type: ReturnableDestinationType;

  /** ---- Vendor ---- */
  vendor_id?: string;
  vendor_name?: string;
  vendor_warehouse_id?: string;
  vendor_warehouse_address?: AddressModel;

  /** ---- Customer ---- */
  customer_id?: string;
  customer_name?: string;
  billing_address?: AddressModel;
  shipping_address?: AddressModel;

  expected_dispatch_date: Date;
  expected_return_date: Date;

  items: ItemWarehouseModel[];

  description?: string;
  purpose: string;
  contact_name: string;
  contact_number: string;
}

export interface CreateFoCSTPayload {
  from_warehouse_id: string;
  from_warehouse_name: string;
  shipping_details?: {
    logistic_partner?: string;
    tracking_id?: string;
  };
  destination_type: ReturnableDestinationType;

  customer_id?: string;
  customer_name?: string;
  billing_address?: AddressModel;
  shipping_address?: AddressModel;

  expected_dispatch_date: Date;

  items: ItemWarehouseModel[];

  description?: string;
  purpose: string;
  contact_name: string;
  contact_number: string;
}

export interface CreateReplacementSTPayload {
  complaint_id: string;
  service_id?: string;
  from_warehouse_id: string;
  from_warehouse_name: string;
  shipping_details?: {
    logistic_partner?: string;
    tracking_id?: string;
  };
  expected_dispatch_date?: Date;
  expected_return_date?: Date;
  /** ---- Customer ---- */
  customer_id?: string;
  customer_name?: string;
  billing_address?: AddressModel;
  shipping_address?: AddressModel;
  contact_name: string;
  contact_number: string;
  expected_date: Date;
  faulty_items: ItemWarehouseModel[];
  items: ItemWarehouseModel[];
  linked_order_id: string;
  description?: string;
  purpose: string;
  inward_status?: "pending" | "partial" | "completed";
  item_type?: "FG" | "RM"; // Finished Goods or Raw Material
}

export enum StockTransferSubtype {
  REGULAR = "Regular",
  RETURNABLE = "Returnable",
  REPLACEMENT = "Replacement",
  FOC = "FoC",
  REVERSE_DC = "Reverse DC",
}

export type ReturnableDCSourceType = "SO" | "PO" | "ST";

export interface ReturnableDCSourceItem {
  item_code: string;
  item_name: string;
  subcategory: string;
  unit: string;
  hsn: string;
  delivered_qty: number;
  price: number;
}

export interface ReturnableDCSource {
  source_order_type: ReturnableDCSourceType;
  order_id: string;
  counterparty: { id?: string; name?: string; gst?: string };
  from_address?: AddressModel;
  items: ReturnableDCSourceItem[];
  match_key?: string;
  match_label?: string;
}

// Response of the by-customer/consolidated endpoint: every delivered SO in
// the date range, already fetched and merged server-side.
export interface ReturnableDCConsolidatedItem extends ReturnableDCSourceItem {
  source_order_ids: string[];
}

export interface ReturnableDCConsolidatedResult {
  items: ReturnableDCConsolidatedItem[];
  linkedOrderIds: string[];
  matchKey?: string;
  matchLabel?: string;
  fromAddress?: AddressModel;
  skipped: number;
}

export interface CreateReturnableDCPayload {
  source_order_type: ReturnableDCSourceType;
  linked_order_ids: string[];

  customer_id?: string;
  customer_name?: string;

  // FROM (pickup) address — auto-filled from source order, editable.
  shipping_address?: AddressModel;

  // TO = Kazam warehouse the goods are received into.
  to_warehouse_id: string;
  to_warehouse_name: string;

  contact_name: string;
  contact_number: string;
  purpose: string;
  description?: string;

  items: ItemWarehouseModel[];

  shipping_details?: {
    logistic_partner?: string;
    tracking_id?: string;
  };
}

export interface CustomerDetails {
  uid?: string;
  first_name?: string;
  last_name?: string;
  mobile?: string;
  email?: string;
  customer_group?: string;
  customer_type?: string;
  store_name?: string;
  brand?: string;
}

export interface BaseWarehouseOutward extends BaseModel {
  type: "SO" | "ST";

  customerDetails?: CustomerDetails;

  status?: WarehouseOutwardStatus;

  sync_pending?: boolean;

  from_warehouse_id?: string;
  from_warehouse_name?: string;
  gst_address?: any;

  shipping_details?: {
    logistic_partner: string;
    tracking_id: string;
    tracking_status?: string;
    tracking_status_raw?: string;
    tracking_meta?: {
      location?: string;
      scan_remark?: string;
      scan_timestamp?: string;
      delivered_date?: string;
      checked_at?: string;
    };
    delhivery_synced_at?: string;
    delhivery_sync_error?: string;
    delhivery_sync_attempts?: number;
  };
  delivered_date?: Date | string | null;

  attachments?: AttachmentModel[];
  dc_attachment?: DCAttachmentModel;
  invoice_attachment?: DCAttachmentModel;
  invoice_material_attachment?: DCAttachmentModel;
  eway_attachment?: DCAttachmentModel;
  dc_ambill_attachment?: DCAttachmentModel;
  eway_waived?: boolean;
  items: ItemWarehouseModel[];

  po_no?: string;
  narration?: string;

  rejection_reason?: string;
  rejected_at?: Date;
  rejected_by?: string;
  rejected_by_name?: string;
  dispatched_date?: Date | string | null;
  dispatched_at?: Date | string | null;
}

export type SuggestedTransportMode = "Surface" | "Air" | "Train";

export interface SOWarehouseOutward extends BaseWarehouseOutward {
  type: "SO";
  order_id?: string;
  customer_name?: string;
  sale_type?: string;
  destination_address: AddressModel;
  org_namespace?: string;
  org_name?: string;
  suggested_transport_mode?: SuggestedTransportMode;
  warehouse_suggestion_remarks?: string;
}

export interface BaseSTWarehouseOutward extends BaseWarehouseOutward {
  type: "ST";
  purpose: string;
  description?: string;
  received_items?: ItemWarehouseModel[];
  is_inter_state_transfer?: boolean;
  is_internal?: boolean;
  contact_name: string;
  contact_number: string;
}

export interface RegularSTOutward extends BaseSTWarehouseOutward {
  st_subtype: StockTransferSubtype.REGULAR;
  to_warehouse_id: string;
  to_warehouse_name: string;
  expected_date?: Date;
}

export enum ReturnableDestinationType {
  VENDOR = "Vendor",
  CUSTOMER = "Customer",
}

export interface ReturnableSTOutward extends BaseSTWarehouseOutward {
  st_subtype: StockTransferSubtype.RETURNABLE;

  destination_type: ReturnableDestinationType;

  vendor_id?: string;
  vendor_name?: string;
  vendor_warehouse_id?: string;
  vendor_warehouse_address?: AddressModel;

  customer_id?: string;
  customer_name?: string;
  billing_address?: AddressModel;
  shipping_address?: AddressModel;

  expected_dispatch_date: Date;
  expected_return_date: Date;

  converted_order_id?: string;
}

export interface FoCSTOutward extends BaseSTWarehouseOutward {
  st_subtype: StockTransferSubtype.FOC;

  destination_type: ReturnableDestinationType.CUSTOMER;

  customer_id?: string;
  customer_name?: string;
  billing_address?: AddressModel;
  shipping_address?: AddressModel;

  expected_dispatch_date: Date;
}

export interface ReplacementSTOutward extends BaseSTWarehouseOutward {
  st_subtype: StockTransferSubtype.REPLACEMENT;
  complaint_id: string;
  customer_id: string;
  customer_name: string;
  service_id?: string;
  billing_address: AddressModel;
  shipping_address: AddressModel;

  linked_order_id: string;
  faulty_items: ItemWarehouseModel[];
  expected_date: Date;
  inward_status?: "pending" | "partial" | "completed";
  item_type?: "FG" | "RM"; // Finished Goods or Raw Material

  // --- Complaint-to-Replacement pipeline ---
  replacement_request_id?: string;
  hw_approved_by?: string;
  hw_approved_by_name?: string;
  hw_approved_at?: Date;
  hw_rejection_reason?: string;
  biz_approved_by?: string;
  biz_approved_by_name?: string;
  biz_approved_at?: Date;
  biz_rejection_reason?: string;
  linked_reverse_dc_id?: string;
  reverse_dc_creation_failed?: boolean;
  reverse_dc_creation_error?: string;
}

export interface ReturnableDCOutwardModel extends BaseSTWarehouseOutward {
  st_subtype: StockTransferSubtype.REVERSE_DC;
  source_order_type: ReturnableDCSourceType;
  linked_order_ids: string[];
  linked_replacement_st_id?: string;
  customer_id?: string;
  customer_name?: string;
  shipping_address?: AddressModel;
  to_warehouse_id: string;
  to_warehouse_name: string;
  billing_address?: AddressModel;
  expected_date?: Date;
}

export type WarehouseOutwardModel =
  | SOWarehouseOutward
  | RegularSTOutward
  | ReturnableSTOutward
  | FoCSTOutward
  | ReplacementSTOutward
  | ReturnableDCOutwardModel;

export interface DCAttachmentModel {
  id: string;
  category: string;
  type: string;
  url: string;
  filename?: string;
  created_at: Date;
  created_by: string;
  created_by_name: string;
}

export enum LogisticPartner {
  DELHIVERY = "Delhivery",
  DART_EXPRESS = "Dart Express",
  PORTAL = "Portal",
  DTDC = "DTDC",
  OTHERS = "Others",
}

export const LOGISTIC_PARTNER_OPTIONS: { label: string; value: string }[] = [
  { label: LogisticPartner.DELHIVERY, value: LogisticPartner.DELHIVERY },
  { label: LogisticPartner.DART_EXPRESS, value: LogisticPartner.DART_EXPRESS },
  { label: LogisticPartner.PORTAL, value: LogisticPartner.PORTAL },
  { label: LogisticPartner.DTDC, value: LogisticPartner.DTDC },
  { label: LogisticPartner.OTHERS, value: LogisticPartner.OTHERS },
];

const PREDEFINED_LOGISTIC_PARTNERS: string[] = [
  LogisticPartner.DELHIVERY,
  LogisticPartner.DART_EXPRESS,
  LogisticPartner.PORTAL,
  LogisticPartner.DTDC,
];

// Split a stored logistic partner string into the dropdown selection + the
// free-text "Others" value. Any non-predefined value is treated as "Others".
export const splitLogisticPartner = (
  stored?: string,
): { selection: string; otherValue: string } => {
  const value = (stored || "").trim();
  if (!value) return { selection: "", otherValue: "" };
  if (PREDEFINED_LOGISTIC_PARTNERS.includes(value)) {
    return { selection: value, otherValue: "" };
  }
  return { selection: LogisticPartner.OTHERS, otherValue: value };
};

export enum WarehouseOutwardStatus {
  WH_YET_TO_BE_ASSIGNED = "WH Yet To Be Assigned",
  WH_ASSIGNED = "WH Assigned",
  ST_FG_APPROVAL_PENDING = "ST FG Approval Pending",
  // Complaint-to-Replacement pipeline only (gate-created Replacement STs).
  DRAFT = "Draft",
  HW_APPROVAL_PENDING = "HW Approval Pending",
  BUSINESS_APPROVAL_PENDING = "Business Approval Pending",
  DEVICE_ALLOCATION_PENDING = "Device Allocation Pending",
  MATERIAL_PICKING_PENDING = "Material Picking Pending",
  DOC_PENDING = "Doc Pending",
  READY_TO_DISPATCH = "Ready To Dispatch",
  TRACKING_ID_PENDING = "Tracking ID Pending",
  DISPATCHED = "Dispatched",
  DELIVERED = "Delivered",
  REJECTED = "Rejected",
  CANCELLED = "Cancelled",
  RETURNED = "Returned",
}

export interface UpdateSTPayload {
  to_warehouse_id: string;
  to_warehouse_name: string;
  expected_date: Date;
  purpose?: string;
  description?: string;
  items: Array<{
    item_code: string;
    qty: number;
    price: number;
    device_ids?: string[];
  }>;
}

export const normalizeStockTransferSubtype = (
  subtype?: string,
): StockTransferSubtype | undefined => {
  if (!subtype) return undefined;
  const normalized = subtype.trim().toLowerCase();

  if (normalized === "regular") return StockTransferSubtype.REGULAR;
  if (normalized === "returnable") return StockTransferSubtype.RETURNABLE;
  if (normalized === "replacement") return StockTransferSubtype.REPLACEMENT;
  if (normalized === "foc" || normalized === "free of cost")
    return StockTransferSubtype.FOC;
  if (normalized === "returnabledc" || normalized === "returnable dc")
    return StockTransferSubtype.REVERSE_DC;

  return undefined;
};

export const isReplacementStockTransferSubtype = (subtype?: string): boolean =>
  normalizeStockTransferSubtype(subtype) === StockTransferSubtype.REPLACEMENT;
